/**
 * What the first screen reads: hg-order's count of what is going on, the
 * applications that wait, and hg-billing's count of the money. Every key
 * starts with `NOW_KEY`.
 *
 * Both services count when asked, so the screen may ask as often as it
 * refreshes: the staff stream says when (`./live.ts`), and `useLiveQuery`
 * asks by itself as a backstop.
 */
import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import { billingApi, contentApi, orderApi, unwrap } from "@/shared/api/clients";

import type { Overview } from "./types";

export const NOW_KEY = ["now"] as const;

/** How long an answer is taken for current when the screen opens. */
const FRESH_MS = 7_500;

/** What is going on now, within the caller's areas or one of them. */
export function overviewQuery(areaId: string | undefined) {
  return queryOptions({
    queryKey: [...NOW_KEY, "overview", areaId ?? null] as const,
    queryFn: async ({ signal }): Promise<Overview> => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/overview", {
          params: { query: areaId === undefined ? {} : { area_id: areaId } },
          signal,
        }),
      );
      return data;
    },
    staleTime: FRESH_MS,
    placeholderData: keepPreviousData,
  });
}

/** How many specialists each status holds: the applications that wait for a decision are among them. */
export function applicationsQuery(areaId: string | undefined) {
  return queryOptions({
    queryKey: [...NOW_KEY, "applications", areaId ?? null] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/specialists/counts", {
          params: { query: areaId === undefined ? {} : { area_id: areaId } },
          signal,
        }),
      );
      return data;
    },
    staleTime: FRESH_MS,
    placeholderData: keepPreviousData,
  });
}

export const MONEY_KEY = [...NOW_KEY, "money"] as const;

/**
 * The money now, from the ledger. hg-billing holds no service areas: an
 * admin sees the whole of it, and a dispatcher is refused (`403`).
 */
export const moneyOverviewQuery = queryOptions({
  queryKey: MONEY_KEY,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(billingApi.GET("/v2/admin/money/overview", { signal }));
    return data;
  },
  staleTime: FRESH_MS,
});

/**
 * The support threads that wait for a reply, and since when the longest
 * has: hg-content's count, of no area.
 */
export const supportQuery = queryOptions({
  queryKey: [...NOW_KEY, "support"] as const,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(contentApi.GET("/v2/admin/support/threads/counts", { signal }));
    return data;
  },
  staleTime: FRESH_MS,
});
